// Shell-level floating dock for the Org Activity card — Phase 6.
// See docs/planning/org_activity_floating_dock.md for the locked design.
//
// Owns:
// - audience + org gating (hidden when no scopedOrganizationId or role lacks access),
// - open/closed state for the expanded panel,
// - pointer-event handling that distinguishes a tap (open panel) from a
//   drag (move + persist position) — uses pointer capture so move/up still
//   fire on the originating element even when the cursor leaves it,
// - data wire-up to WorkspaceUrgencyContext (no new fetch).

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { ActivityDockFireflyTab } from "./ActivityDockFireflyTab";
import { ActivityDockExpandedPanel } from "./ActivityDockExpandedPanel";
import { useActivityDockPosition } from "./useActivityDockPosition";
import { deriveActivityDockStatus } from "./activityDockStatus";
import { formatFireflyStatusReasonBanner } from "./fireflyStatusReason";
import { useActivityDockModePeek } from "./useActivityDockModePeek";
import { useFireflyPttHotkey } from "@/components/activity/firefly/useFireflyPttHotkey";
import { useActivityDockFireflyPtt } from "./useActivityDockFireflyPtt";
import { useActivityDockAssistLevel } from "./useActivityDockAssistLevel";
import { useActivityDockExplicitOpen } from "./useActivityDockExplicitOpen";
import { useActivityDockKeyboardCollapse } from "./useActivityDockKeyboardCollapse";
import { useActivityDockCollapseOnBugReport } from "./useActivityDockCollapseOnBugReport";
import { useActivityDockPeekDecision } from "./useActivityDockPeekSignal";
import { useActivityDockSections } from "./useActivityDockSections";
import { useActivityDockPeekPersistence } from "@/hooks/useActivityDockPeekPersistence";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { DOCK_AUDIENCE_ROLES } from "@/lib/activityDock/dockAudience";
import { ACTIVITY_DOCK_OPEN_EVENT } from "./activityDockOpenEvent";
import type { ActivityFloatingDockProps } from "./ActivityFloatingDockProps";
import { closeDockThen } from "./closeDockThen";
import { useFireflyDisplayMode } from "@/hooks/useFireflyDisplayMode";
import { shouldShowFloatingFireflyTab } from "@/lib/firefly/sidebarFireflyDockGlow";
import { useAttentionList } from "@/components/attention/useAttentionList";
import { publishAttentionChrome } from "@/lib/attention/attentionChrome";
import { useActivityDockPanelRepositionDrag } from "./useActivityDockPanelRepositionDrag";
import {
  isFireflySidebarDockTravelActive,
  setFireflySidebarDockTravelActive,
  subscribeFireflySidebarDockTravel,
} from "@/lib/firefly/fireflySidebarDockTravel";

export { ACTIVITY_DOCK_OPEN_EVENT };
export type { ActivityFloatingDockProps } from "./ActivityFloatingDockProps";

export function ActivityFloatingDock({
  onOpenAdsIngestion,
  onOpenAdsLibrary,
  onOpenAdsAssistant,
  onOpenAdsAssistantSuggestion,
  onOpenSalesConsole,
  followUpAttentionCount,
  onSendAllUnsent,
  isSending,
  currentMode,
  onOpenPlatform,
  kbGapOpenCount,
  onOpenKbGapInbox,
  aiPromptingOpenCount,
  onOpenAiPrompting,
  pendingMemberApprovalCounts,
  pendingMemberApprovalTotal,
  onOpenAdmin,
  orgUnsentRollupCount,
  onOpenDashboard,
  staleWacCount,
  onOpenMfgInventory,
  plantRequestReviewCount,
  negativeOnHandCount,
  plantRequestStatusItems,
  onOpenMfgPurchasing,
  openQcItems,
  onOpenMfgBuilderAssistant,
  onNavigateToMode,
  scopedOrganizationId = null,
  onHighlightFireflyTarget,
  onStartFireflyTour,
  tourDockOverride = null,
}: ActivityFloatingDockProps) {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const urgency = useWorkspaceUrgencyContext();
  const {
    position,
    dragPreview,
    isDragging,
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    tabWidthPx,
    tabHeightPx,
  } = useActivityDockPosition();

  const [open, setOpen] = useState(false);
  const [blinkingSuppressed, setBlinkingSuppressed] = useState(false);
  // Slice B2.1 — lifted up from `ActivityDockExpandedPanel` so the
  // peek-lifecycle hook can attach in-panel interaction listeners
  // alongside the panel's own click-outside listener. Panel still
  // owns its own click-outside / Esc handling via the same ref.
  const panelRef = useRef<HTMLDivElement>(null);
  // Slice B2.2 — user-tunable assist level. Gates both the B2.1
  // mode-change peek (via `useActivityDockModePeek`) and the
  // explicit-event auto-open below.
  const { level: assistLevel } = useActivityDockAssistLevel();
  const { mode: fireflyDisplayMode } = useFireflyDisplayMode();
  const [dockTravelActive, setDockTravelActive] = useState(() =>
    isFireflySidebarDockTravelActive(),
  );
  useEffect(() => subscribeFireflySidebarDockTravel(setDockTravelActive), []);
  // Safety: never leave travel stuck >5s (Firefly tab would stay hidden).
  useEffect(() => {
    if (!dockTravelActive) return;
    const t = window.setTimeout(() => {
      if (isFireflySidebarDockTravelActive()) {
        setFireflySidebarDockTravelActive(false);
      }
    }, 5000);
    return () => window.clearTimeout(t);
  }, [dockTravelActive]);
  const showFloatingFireflyTab =
    shouldShowFloatingFireflyTab(fireflyDisplayMode) && !dockTravelActive && !open;
  const canShowExpandedPanel = !dockTravelActive;
  const tourActive = tourDockOverride?.active ?? false;

  const pttEnabled = assistLevel !== "off" && !tourActive;

  const {
    askRef,
    pttListening,
    tabPttListening,
    pointerHandlers,
    onPttStartHotkey,
    onPttEnd,
  } = useActivityDockFireflyPtt({
    open,
    setOpen,
    assistLevel,
    tourActive,
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
  });

  const panelDragHandlers = useActivityDockPanelRepositionDrag({
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
  });

  useFireflyPttHotkey({
    enabled: pttEnabled,
    onPttStart: onPttStartHotkey,
    onPttEnd,
  });

  const onDockOpened = useCallback(
    (detail: { expandAsk?: boolean } | undefined) => {
      if (!detail?.expandAsk) return;
      window.setTimeout(() => askRef.current?.expandForPtt(), 0);
    },
    [askRef],
  );
  useActivityDockExplicitOpen(assistLevel, setOpen, onDockOpened);

  // Both lifecycle collapsers funnel through the same idempotent
  // `setOpen(false)` callback. The bug-report subscriber gates
  // internally on event source (desktop only); the mobile-keyboard
  // subscriber gates internally on visualViewport detection.
  const closeDock = useCallback(() => setOpen(false), []);
  useActivityDockKeyboardCollapse(closeDock);
  useActivityDockCollapseOnBugReport(closeDock);

  // Slice D — derive unsent count from the same `leads` array the
  // unsent-leads section will render. Computed at the dock level so
  // the collapsed tab can show the count badge without the expanded
  // panel being mounted. Filter mirrors `useRecentLeads`'s
  // `totalUnsent` definition (`!sent && !isTest`).
  const attention = useAttentionList({
    currentMode: currentMode ?? null,
    openQcCount: openQcItems?.length ?? 0,
    negativeOnHandCount: negativeOnHandCount ?? 0,
    plantRequestReviewCount: plantRequestReviewCount ?? 0,
    plantStatusCount: plantRequestStatusItems?.length ?? 0,
    staleWacCount: staleWacCount ?? 0,
    kbGapOpenCount: kbGapOpenCount ?? 0,
    aiPromptingOpenCount: aiPromptingOpenCount ?? 0,
    pendingMemberApprovalTotal: pendingMemberApprovalTotal ?? 0,
    orgUnsentRollupCount: orgUnsentRollupCount ?? 0,
    onOpenLeads: true,
    onOpenBuilderAssistant: Boolean(onOpenMfgBuilderAssistant),
    onOpenInventory: Boolean(onOpenMfgInventory),
    onOpenPurchasing: Boolean(onOpenMfgPurchasing),
    onOpenAds: Boolean(onOpenAdsIngestion),
    onOpenKb: Boolean(onOpenKbGapInbox),
    onOpenAiPrompting: Boolean(onOpenAiPrompting),
    onOpenAdmin: Boolean(onOpenAdmin),
  });

  const status = deriveActivityDockStatus({
    activity: urgency?.adsActivity,
    auth,
    unsentCount: attention.unsentCount,
    plantRequestReviewCount: plantRequestReviewCount ?? 0,
    negativeOnHandCount: negativeOnHandCount ?? 0,
    openQcCount: openQcItems?.length ?? 0,
    staleWacCount: staleWacCount ?? 0,
    kbGapOpenCount: kbGapOpenCount ?? 0,
    aiPromptingOpenCount: aiPromptingOpenCount ?? 0,
    pendingMemberApprovalTotal: pendingMemberApprovalTotal ?? 0,
    landingReady: attention.landingReady,
  });
  const fireflyStatusReason = formatFireflyStatusReasonBanner(
    status.tone,
    status.reasonLabel,
  );

  useEffect(() => {
    if (status.tone !== "red") {
      setBlinkingSuppressed(false);
    }
  }, [status.tone]);

  useEffect(() => {
    publishAttentionChrome({
      tone: status.tone,
      summary: status.summary,
      reasonLabel: status.reasonLabel,
      panelOpen: open,
    });
  }, [open, status.reasonLabel, status.summary, status.tone]);

  const currentBucket = currentMode ? getBucketForMode(currentMode) : null;
  const dockSections = useActivityDockSections(currentBucket);

  // Slice B2.4 — section-aware peek decision: returns both the
  // boolean "should peek?" and the `reasonSectionId` (Slice B2.4-B)
  // identifying which section's signal earned the peek. The reason
  // is forwarded to the structured `ACTIVITY_DOCK_PEEK_EVENT`
  // payload the hook emits on fire.
  const peekDecision = useActivityDockPeekDecision(
    currentMode,
    { kbGapOpenCount, aiPromptingOpenCount },
    { pendingMemberApprovalTotal },
    { staleWacCount, plantRequestReviewCount, openQcCount: openQcItems?.length ?? null },
  );

  // Slice B2.1 — transient peek on mode entry. Hook is a no-op when
  // `currentMode` is absent or when no section signals peek-worthy
  // urgency, so existing audience-but-quiet states (no urgency)
  // don't get hijacked into auto-opening. Slice B2.2 layers the
  // user's assist-level on top: `off` / `minimal` skip the peek
  // entirely.
  useActivityDockModePeek({
    currentMode,
    peekDecision,
    open,
    setOpen,
    panelRef,
    assistLevel,
  });

  // Slice B2.4-D — best-effort cross-session persist on each peek fire.
  useActivityDockPeekPersistence(currentMode);

  // WF-0 §6.1 Q3 lean — no registry-eligible sections ⇒ no tab chrome.
  if (!scopedOrgId) return null;
  if (!auth.session) return null;
  if (!auth.hasRole(...DOCK_AUDIENCE_ROLES)) return null;
  if (dockSections.length === 0) return null;

  // RP-5 — pull impersonation state once at the dock level and pass it
  // to both children. Reading `auth.impersonation` here (rather than
  // inside the tab + panel separately) keeps the prop wiring symmetric
  // with the rest of this component's auth/urgency forwarding and
  // means the tab + panel see a consistent impersonation snapshot from
  // the same render frame.
  const impersonating = auth.impersonation.active;
  const impersonatingRoles = auth.impersonation.roles;

  return (
    <>
      {showFloatingFireflyTab ? (
        <ActivityDockFireflyTab
          edge={position.edge}
          offsetRatio={position.offsetRatio}
          dragPreview={dragPreview}
          tabWidthPx={tabWidthPx}
          tabHeightPx={tabHeightPx}
          tone={status.tone}
          summary={status.summary}
          ariaLabel={status.ariaLabel}
          statusReasonTitle={fireflyStatusReason}
          expanded={open}
          isDragging={isDragging}
          blinkingSuppressed={blinkingSuppressed}
          impersonating={impersonating}
          impersonatingRoles={impersonatingRoles}
          onPointerDown={pointerHandlers.onPointerDown}
          onPointerMove={pointerHandlers.onPointerMove}
          onPointerUp={pointerHandlers.onPointerUp}
          onPointerCancel={pointerHandlers.onPointerCancel}
          tourDockOverride={tourDockOverride}
          pttListening={pttListening}
        />
      ) : null}

      {open && canShowExpandedPanel ? (
        <ActivityDockExpandedPanel
          edge={position.edge}
          offsetRatio={position.offsetRatio}
          tabWidthPx={tabWidthPx}
          tabHeightPx={tabHeightPx}
          dragPreview={dragPreview}
          panelDragHandlers={panelDragHandlers}
          outerRef={panelRef}
          onClose={() => setOpen(false)}
          onOpenAdsIngestion={closeDockThen(setOpen, onOpenAdsIngestion)}
          onOpenAdsLibrary={closeDockThen(setOpen, onOpenAdsLibrary)}
          onOpenAdsAssistant={closeDockThen(setOpen, onOpenAdsAssistant)}
          onOpenAdsAssistantSuggestion={closeDockThen(setOpen, onOpenAdsAssistantSuggestion)}
          onOpenSalesConsole={closeDockThen(setOpen, onOpenSalesConsole)}
          followUpAttentionCount={followUpAttentionCount}
          onSendAllUnsent={onSendAllUnsent}
          isSending={isSending}
          currentMode={currentMode ?? null}
          onOpenPlatform={closeDockThen(setOpen, onOpenPlatform)}
          kbGapOpenCount={kbGapOpenCount}
          onOpenKbGapInbox={closeDockThen(setOpen, onOpenKbGapInbox)}
          aiPromptingOpenCount={aiPromptingOpenCount}
          onOpenAiPrompting={closeDockThen(setOpen, onOpenAiPrompting)}
          pendingMemberApprovalCounts={pendingMemberApprovalCounts}
          pendingMemberApprovalTotal={pendingMemberApprovalTotal}
          onOpenAdmin={closeDockThen(setOpen, onOpenAdmin)}
          orgUnsentRollupCount={orgUnsentRollupCount}
          onOpenDashboard={closeDockThen(setOpen, onOpenDashboard)}
          staleWacCount={staleWacCount}
          onOpenMfgInventory={closeDockThen(setOpen, onOpenMfgInventory)}
          plantRequestReviewCount={plantRequestReviewCount}
          plantRequestStatusItems={plantRequestStatusItems}
          onOpenMfgPurchasing={closeDockThen(setOpen, onOpenMfgPurchasing)}
          openQcItems={openQcItems}
          onOpenMfgBuilderAssistant={closeDockThen(setOpen, onOpenMfgBuilderAssistant)}
          impersonating={impersonating}
          impersonatingRoles={impersonatingRoles}
          fireflyStatusTone={status.tone}
          fireflyStatusReasonLabel={status.reasonLabel}
          showStopBlinking={status.tone === "red" && !blinkingSuppressed}
          onStopBlinking={() => setBlinkingSuppressed(true)}
          onNavigateToMode={closeDockThen(setOpen, onNavigateToMode)}
          scopedOrganizationId={scopedOrganizationId}
          onHighlightFireflyTarget={onHighlightFireflyTarget}
          onStartFireflyTour={onStartFireflyTour}
          askRef={askRef}
          tabPttListening={tabPttListening}
          bellHost={fireflyDisplayMode === "docked"}
          negativeOnHandCount={negativeOnHandCount}
        />
      ) : null}
    </>
  );
}
